import {
    AuctionsNearMeQueryParams,
    AuctionsNearMeRouteParams,
} from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import { createBoundingBoxString } from '@/pages/AuctionsNearMe/helpers/createBoundingBoxString';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultHitsPerPage,
    defaultPageNumber,
    MAX_CATALOGS_TO_FETCH,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { findGeoAreaFromUrlParams } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { getCatalogsNearMeSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { loadAllGeoAreas } from '@/redux/modules/geoArea/geoArea';
import { MapBounds } from '@/pages/AuctionsNearMe/components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { parseQueryParams } from '@/utils/queryParams';

const auctionsNearMePageData = async ({ location, match, store }: DataLoaderParams<AuctionsNearMeRouteParams>) => {
    const { dispatch, getState } = store;

    // Parse query parameters to check for custom search
    const queryParams = parseQueryParams<AuctionsNearMeQueryParams>(location.search);

    // If there's a search query, skip fetching - the component will handle it via geocoding
    if (queryParams.search) {
        // Still load geoAreas and fetch ad, but don't fetch catalogs
        await Promise.all([
            dispatch(loadAllGeoAreas()),
            dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' })),
        ]);
        return;
    }

    let mapBounds: MapBounds =
        getState().intl.locale.toLowerCase() === 'en-gb'
            ? defaultAuctionsNearMeUkBounds
            : defaultAuctionsNearMeUsaBounds;

    await dispatch(loadAllGeoAreas());

    // Find geoArea from URL params (city/state/country)
    const allGeoAreas = Object.values(getState().geoArea.byId);
    const geoArea = findGeoAreaFromUrlParams(allGeoAreas, {
        city: match.params.city,
        country: match.params.country,
        state: match.params.state,
    });

    if (geoArea) {
        mapBounds = {
            northEast: {
                lat: geoArea.northEastLatitude,
                lng: geoArea.northEastLongitude,
            },
            southWest: {
                lat: geoArea.southWestLatitude,
                lng: geoArea.southWestLongitude,
            },
        };
    }

    // Fetch catalogs within the map bounds
    const insideBoundingBox = createBoundingBoxString(mapBounds);

    await Promise.all([
        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: defaultPageNumber,
                pageSize: MAX_CATALOGS_TO_FETCH,
            })
        ),
        dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' })),
    ]);

    // Fetch full catalog details for the first page of results (needed for SSR)
    const catalogsNearMe = getCatalogsNearMeSelector(getState());
    const firstPageCatalogIds = catalogsNearMe.slice(0, defaultHitsPerPage).map((catalog) => catalog.id);

    if (firstPageCatalogIds.length > 0) {
        await dispatch(fetchCatalogsIfNeeded({ catalogIds: firstPageCatalogIds }));
    }
};

export default auctionsNearMePageData;
